import { faroCoreApiClient } from "@api/faro-core-api-client";
import {
  createMemberOption,
  createTeamOption,
} from "@components/common/members-autocomplete/members-autocomplete-utils";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { RoleDisplayNames } from "@custom-types/member-types";
import { SdbProject } from "@custom-types/project-types";
import { type AutoCompleteOption } from "@flat-ui";
import { useMembersAutocomplete } from "@hooks/autocomplete/use-members-autocomplete";
import { useCompanyMembers } from "@hooks/use-company-members";
import { useRespondToInvite } from "@hooks/use-respond-to-invite";
import { useTeamUtils } from "@hooks/use-team-utils";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { setOne } from "@store/members/members-slice";
import { convertToMemberTypes } from "@store/members/members-slice-utils";
import { projectMembersSelector } from "@store/projects/projects-selector";
import {
  addProcessingProjects,
  removeProcessingProjects,
  setOneProjects,
} from "@store/projects/projects-slice";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { teamsSelector } from "@store/teams/teams-selector";
import { sortMembersByNameOrEmail } from "@utils/member-utils";
import { shouldDisableSendInvite } from "@utils/members-invite-utils";
import { isTeam, sortTeams } from "@utils/team-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useMemo, useState } from "react";

type ProjectRole = FaroCoreApiTypes.AssignmentRoleBySubjectType["project"];

interface UseInviteMembersToProjectParams {
  /** Project the members are being invited to. */
  project: SdbProject;

  /**
   * Role selected initially and restored after a successful invite.
   * @default undefined
   */
  initialRole?: ProjectRole;

  /**
   * Invoked after members are successfully invited (e.g. to close a dialog).
   * The loading state is turned off only after this callback resolves, so
   * awaiting a closing animation here prevents duplicate submissions.
   */
  onInviteSuccess?(): void | Promise<void>;

  /** Location from which the invite members action is triggered, for tracking purposes. */
  location: "share-project-dialog" | "invite-member-to-project";
}

interface UseInviteMembersToProjectResult {
  /** Combined team and member options for the autocomplete. */
  autocompleteOptions: AutoCompleteOption[];

  /** Currently selected autocomplete options. */
  selectedMembers: ReturnType<typeof useMembersAutocomplete>["selectedMembers"];

  /** Helper text to display below the autocomplete input. */
  helperText: ReturnType<typeof useMembersAutocomplete>["helperText"];

  /** Updates the selected options. */
  handleMemberSelect: ReturnType<
    typeof useMembersAutocomplete
  >["handleMemberSelect"];

  /** Validates the typed text in the autocomplete input. */
  onInputChange: ReturnType<typeof useMembersAutocomplete>["onInputChange"];

  /** Validates a value used by the autocomplete `isValid` prop. */
  isValidInput(value: string): boolean;

  /** Currently selected project role. */
  selectedRole?: ProjectRole;

  /** Updates the selected project role. */
  setSelectedRole(role: ProjectRole): void;

  /** Whether the invite request is in progress. */
  isLoading: boolean;

  /** Whether the send invite action should be disabled. */
  shouldSendInviteDisable: boolean;

  /** Submits the selected members and role to invite them to the project. */
  handleConfirm(): Promise<void>;

  /** Resets the selection and input validity. */
  reset(): void;
}

/**
 * Encapsulates the shared logic for inviting members and teams to a project:
 * building the autocomplete options, wiring the members autocomplete, deriving
 * the disabled state and submitting the invitation. Consumers only render the
 * surrounding UI (dialog, inline form, etc.).
 */
export function useInviteMembersToProject({
  project,
  initialRole,
  onInviteSuccess,
  location,
}: UseInviteMembersToProjectParams): UseInviteMembersToProjectResult {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { isTeamName } = useTeamUtils();
  const { trackEvent } = useTrackEvent();
  const companyMembers = useCompanyMembers();
  const teams = useAppSelector(teamsSelector);
  const { respondToUser } = useRespondToInvite();
  const { handleErrorWithToast } = useErrorContext();
  const projectMembers = useAppSelector(projectMembersSelector);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRole, setSelectedRole] = useState<ProjectRole | undefined>(
    initialRole
  );

  const projectMemberMap = useMemo(
    () => new Map(projectMembers.map((member) => [member.identity, member])),
    [projectMembers]
  );

  const companyMemberIdentities = useMemo(
    () =>
      new Set(companyMembers.map((companyMember) => companyMember.identity)),
    [companyMembers]
  );

  /** Reforming company members data for stellar autocomplete component */
  const memberOptions: AutoCompleteOption[] = useMemo(
    () =>
      sortMembersByNameOrEmail(companyMembers).map((member) => {
        const alreadyInProject = projectMemberMap.get(member.identity);

        return createMemberOption({
          member,
          isDisabled: !!alreadyInProject,
          disabledMessage: alreadyInProject
            ? `Already in project as ${RoleDisplayNames[alreadyInProject.role]}`
            : undefined,
        });
      }),
    [companyMembers, projectMemberMap]
  );

  /** Reforming team data for stellar autocomplete component */
  const teamOptions: AutoCompleteOption[] = useMemo(
    () =>
      sortTeams(teams, "name_ascending").map((team) => {
        const alreadyInProject = projectMemberMap.get(team.id);

        return createTeamOption({
          team,
          isDisabled: !!alreadyInProject,
          disabledMessage: alreadyInProject
            ? `Already in project as ${RoleDisplayNames[alreadyInProject.role]}`
            : undefined,
        });
      }),
    [projectMemberMap, teams]
  );

  const autocompleteOptions: AutoCompleteOption[] = useMemo(
    () => [...teamOptions, ...memberOptions],
    [teamOptions, memberOptions]
  );

  const {
    selectedMembers,
    uniqueSelectedValues,
    helperText,
    isMemberInputValid,
    handleMemberSelect,
    onInputChange,
    isNewEmailValid,
    reset,
  } = useMembersAutocomplete({
    entityName: "project",
    existingMembers: projectMembers,
    options: autocompleteOptions,
    companyMemberIds: companyMembers.map((member) => member.identity),
  });

  /** Disable the `Send Invite` action if no role or no valid member is selected */
  const shouldSendInviteDisable =
    !selectedRole ||
    isLoading ||
    shouldDisableSendInvite(uniqueSelectedValues, isMemberInputValid);

  /** Adds a newly invited member to the members store when needed. */
  function addNewMemberToStore(
    members: FaroCoreApiTypes.AddMembersToProjectResponse["data"]["members"]
  ): void {
    if (!members) {
      return;
    }

    const newMemberResponse = members.find(
      (allMembers) =>
        !projectMembers.some(
          (oldMembers) => oldMembers.identity === allMembers.identity
        )
    );

    if (!newMemberResponse || isTeam(newMemberResponse)) {
      return;
    }

    const isAlreadyExistsInWorkspace = companyMemberIdentities.has(
      newMemberResponse.identity
    );

    if (!isAlreadyExistsInWorkspace) {
      /**
       * The new member should be added to the member store as well.
       * We should use the setOne because we are updating the company role for all members
       * that are present in this list for a Project Role and we should have a Company Role.
       * If we update the role to project in the members store, we will not be able to remove
       * these members from the member page. The user needs to reload the page to have the company
       * role again.
       */
      dispatch(setOne(convertToMemberTypes(newMemberResponse)));
    }
  }

  /** Submit members to be invited */
  async function handleConfirm(): Promise<void> {
    if (isLoading || !selectedRole || !uniqueSelectedValues.length) {
      return;
    }

    setIsLoading(true);

    trackEvent({
      name: ProjectEvents.inviteMember,
      props: {
        location,
        numberOfMembers: uniqueSelectedValues.length,
        role: selectedRole,
      },
    });

    const payload: FaroCoreApiTypes.BulkMemberInvitePayload<"project"> = {
      assignments: [{ role: selectedRole, identities: uniqueSelectedValues }],
    };

    try {
      // Add the projectId to processing projects to start the loading spinner in the project
      dispatch(addProcessingProjects([project.id]));

      const response: FaroCoreApiTypes.AddMembersToProjectResponse =
        await faroCoreApiClient.projects.addMembersToProject({
          companyId,
          projectId: project.id,
          payload,
        });

      // Add invited member to the store. response.data.members exist when the response is either success or warning
      if (response.data.members) {
        dispatch(
          setOneProjects({ ...project, members: response.data.members })
        );
        addNewMemberToStore(response.data.members);
      }

      // Remove projectId from processing project to stop the loading spinner in the project
      dispatch(removeProcessingProjects([project.id]));

      respondToUser(response, uniqueSelectedValues.length);

      await onInviteSuccess?.();

      reset();
      setSelectedRole(initialRole);
    } catch (error) {
      handleErrorWithToast({
        id: `inviteMember-${Date.now().toString()}`,
        title: "Cannot invite the member",
        error,
      });
    } finally {
      setIsLoading(false);
    }
  }

  return {
    autocompleteOptions,
    selectedMembers,
    helperText,
    handleMemberSelect,
    onInputChange,
    isValidInput: (value) => isNewEmailValid(value) || isTeamName(value),
    selectedRole,
    setSelectedRole,
    isLoading,
    shouldSendInviteDisable,
    handleConfirm,
    reset,
  };
}
